/**
 * Dashboard Tabs Settings Tab Component (CR-4441)
 * Allows admins to configure Sales Dashboard and Forecast Forward visibility.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  LayoutDashboard, Building2, TrendingUp, LineChart, Truck,
  type LucideIcon,
} from 'lucide-react';
import {
  CONFIGURABLE_DASHBOARD_TABS,
  CUSTOMER_DASHBOARD_TAB_DEFAULTS,
  SUPPLIER_DASHBOARD_TAB_DEFAULTS,
} from '../../dashboard/types';
import type { DashboardTabsVisibility } from './types';

const TAB_ICONS: Record<string, LucideIcon> = {
  'sales-orders': LineChart,
  'forecast-forward': TrendingUp,
};

interface DashboardTabsTabProps {
  dashboardTabs: DashboardTabsVisibility;
  customerDashboardTabs: DashboardTabsVisibility;
  saving: boolean;
  onDashboardTabsChange: (tabs: DashboardTabsVisibility) => void;
  onCustomerDashboardTabsChange: (tabs: DashboardTabsVisibility) => void;
  onSave: () => void;
}

export function DashboardTabsTab({
  dashboardTabs,
  customerDashboardTabs,
  saving,
  onDashboardTabsChange,
  onCustomerDashboardTabsChange,
  onSave,
}: DashboardTabsTabProps) {
  const supplierEnabled = CONFIGURABLE_DASHBOARD_TABS.filter(
    (tab) => dashboardTabs[tab.id] ?? SUPPLIER_DASHBOARD_TAB_DEFAULTS[tab.id] ?? false,
  ).length;
  const customerEnabled = CONFIGURABLE_DASHBOARD_TABS.filter(
    (tab) => customerDashboardTabs[tab.id] ?? CUSTOMER_DASHBOARD_TAB_DEFAULTS[tab.id] ?? false,
  ).length;
  const totalCount = CONFIGURABLE_DASHBOARD_TABS.length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5" />
            Dashboard Tab Visibility
          </CardTitle>
          <CardDescription>
            Control which dashboard tabs are visible for operating org (supplier) and buyer company users.
          </CardDescription>
          <div className="flex items-center gap-4 pt-2 text-sm text-muted-foreground">
            <span><Truck className="h-4 w-4 inline mr-1" />Supplier: {supplierEnabled}/{totalCount}</span>
            <span><Building2 className="h-4 w-4 inline mr-1" />Customer: {customerEnabled}/{totalCount}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg font-medium text-sm">
            <span className="flex-1">Tab</span>
            <div className="flex gap-8">
              <span className="w-20 text-center flex items-center gap-1"><Truck className="h-4 w-4" />Supplier</span>
              <span className="w-20 text-center flex items-center gap-1"><Building2 className="h-4 w-4" />Customer</span>
            </div>
          </div>

          {CONFIGURABLE_DASHBOARD_TABS.map((tab) => {
            const Icon = TAB_ICONS[tab.id] || LayoutDashboard;
            const supplierChecked = dashboardTabs[tab.id] ?? SUPPLIER_DASHBOARD_TAB_DEFAULTS[tab.id] ?? false;
            const customerChecked = customerDashboardTabs[tab.id] ?? CUSTOMER_DASHBOARD_TAB_DEFAULTS[tab.id] ?? false;
            const customerToggleDisabled = tab.id === 'forecast-forward';

            return (
              <div key={tab.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  <Icon className="h-5 w-5 text-primary" />
                  <div>
                    <Label className="text-base font-medium">{tab.label}</Label>
                    <p className="text-sm text-muted-foreground">{tab.description}</p>
                    {customerToggleDisabled && (
                      <p className="text-xs text-muted-foreground mt-1">Operating org only — not shown to buyer companies.</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-8">
                  <div className="w-20 flex justify-center">
                    <Switch
                      checked={supplierChecked}
                      onCheckedChange={(checked) => onDashboardTabsChange({ ...dashboardTabs, [tab.id]: checked })}
                    />
                  </div>
                  <div className="w-20 flex justify-center">
                    <Switch
                      checked={customerChecked}
                      disabled={customerToggleDisabled}
                      onCheckedChange={(checked) => onCustomerDashboardTabsChange({ ...customerDashboardTabs, [tab.id]: checked })}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <Button onClick={onSave} disabled={saving} className="w-full mt-4">
            {saving ? 'Saving...' : 'Save Dashboard Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
